import type { ReactNode } from 'react';
import { useT } from '@/lib/i18n/useT';

interface OnboardingShellProps {
  step: 1 | 2 | 3;
  title: string;
  subtitle?: string;
  children: ReactNode;
}

const STEP_KEYS = [
  { id: 1, key: 'onboarding.step.account' },
  { id: 2, key: 'onboarding.step.brandProfile' },
  { id: 3, key: 'onboarding.step.brandKit' },
];

export function OnboardingShell({ step, title, subtitle, children }: OnboardingShellProps) {
  const t = useT();
  const STEPS = STEP_KEYS.map((s) => ({ id: s.id, label: t(s.key) }));
  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--color-xk-navy)] via-[var(--color-xk-navy-mid)] to-[var(--color-xk-navy)] px-6 py-10">
      <div className="mx-auto flex max-w-3xl flex-col">
        <header className="mb-10 flex items-center gap-3">
          <img src="/xk-icon.svg" alt="Xotski" className="h-9 w-9 rounded-lg" />
          <span className="font-display text-lg font-bold tracking-tight">xotski</span>
        </header>

        <ol className="mb-10 flex items-center gap-3">
          {STEPS.map((s, idx) => (
            <li key={s.id} className="flex flex-1 items-center gap-3">
              <span
                className={`grid h-8 w-8 place-items-center rounded-full text-sm font-bold transition-colors ${
                  s.id <= step
                    ? 'bg-[var(--color-accent)] text-[var(--color-fg-on-accent)]'
                    : 'bg-[var(--color-bg-elevated)] text-[var(--color-fg-subtle)]'
                }`}
              >
                {s.id}
              </span>
              <span
                className={`hidden text-sm font-semibold sm:inline ${
                  s.id === step ? 'text-[var(--color-fg-primary)]' : 'text-[var(--color-fg-muted)]'
                }`}
              >
                {s.label}
              </span>
              {idx < STEPS.length - 1 && (
                <span
                  className={`h-px flex-1 ${
                    s.id < step ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-border-default)]'
                  }`}
                />
              )}
            </li>
          ))}
        </ol>

        <div className="glass-card p-8">
          <h1 className="mb-2 text-2xl font-bold">{title}</h1>
          {subtitle && (
            <p className="mb-6 text-sm text-[var(--color-fg-muted)]">{subtitle}</p>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
